import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { LoadingState } from '@tsin-core/entities';
import { ApplicationService } from '@tsin-core/services/http/application.service';
import { determineEligibility } from '@tsin-shared/utils/eligibility';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { IAttendance, IMMIInterviewees } from '../administration/administration.model';
import { AuthState } from '../auth.state';
import { PreScreeningState } from '../pre-screening/pre-screening.state';
import { ProExamState } from '../pro-exam/pro-exam.state';
import {
  AddReferenceNote,
  AdminResendRefereeEmail,
  ApplicationsAction,
  AssignApplicationsToCohort,
  ClearAllApplicationSteps,
  ClearApplications,
  ClearSelectedProApplication,
  DeleteReferees,
  GetAdminApplicationByNo,
  GetAdminData,
  GetAllApplicationSteps,
  GetAllProExamApplications,
  GetAllProExamApplicationsSilent,
  GetApplicationRecord,
  GetApplicationReferees,
  GetApplications,
  GetEligibilityDetails,
  GetExportedApplications,
  GetFlagDetails,
  GetFlags,
  GetMMIApplications,
  GetMMIInterviewees,
  GetPhase2ApplicationRecord,
  GetPreviousApplications,
  GetProApplications,
  GetReviewHistory,
  GetSingleStepApplication,
  InitiateApplication,
  MarkAttendance,
  MarkInReviewApplication,
  OpenPhase2Application,
  ResendApplicantRefereeEmail,
  ResendReferenceEmail,
  ResendSingleReferenceEmail,
  ResendSingleRotationEquivalences,
  SaveAdminStep,
  SaveApplicationLanguage,
  SaveFlag,
  SavePhase2AdminStep,
  SavePhaseTwoStep,
  SaveSinglePhaseTwoStep,
  SaveStep,
  SelectProApplication,
  SelectProReviewApplication,
  SetApplicationDecision,
  SetNeedClinicalDirector,
  SetPendingApplicationLanguage,
  SubmitApplication,
  SubmitPro2Application,
  UnSubmitApplication,
  UnSubmitPhase2Application,
  UpdateFlagResolution,
  UpdateSinglePhaseTwoStep,
} from './applications.actions';
import { ApplicationReferee, isApplicationWithdrawn, Phase2ApplicationAdminSteps, PreviousApplication, ProApplicationReview, ProApplicationReviewStep, ProApplicationStatusEnum, ProUserApplication, ReviewHistoryEntry } from './applications.model';
// import { LocaleService } from '@tsin-core/services/common/locale.service';

export class ApplicationsStateModel {
  public applications: ProUserApplication[] = [];
  public submittedApplications: ProUserApplication[] = [];
  public selectedAdminData: any = null;
  public applicationSteps: any[] = [];
  public selectedApplication: ProUserApplication | null = null;
  public reviewApplication: ProApplicationReview | null = null;
  public singleCreatedReferee: any = null;
  public needClinicalDirectorReview: boolean = false;
  public mmiApplications: any[] = [];
  public exportedApplications: any = null;
  public eligibilityDetails: any = null;
  public mmiInterviewees: IMMIInterviewees[] = [];
  public reviewPhase2: Phase2ApplicationAdminSteps | null = null;
  public loading!: LoadingState;
  public flags: any[] = [];
  /** Review audit trail of the application whose review count was opened.
   *  Piste d'audit des révisions de la candidature consultée. */
  public reviewHistory: ReviewHistoryEntry[] = [];
  public reviewHistoryLoading: LoadingState = LoadingState.notLoading;
  /** Earlier applications of the candidate whose count was opened.
   *  Candidatures antérieures du candidat consulté. */
  public previousApplications: PreviousApplication[] = [];
  public previousApplicationsLoading: LoadingState = LoadingState.notLoading;
  /** Referees of the application whose "Referees Status" cell was opened.
   *  Répondants de la candidature dont le statut a été consulté. */
  public applicationReferees: ApplicationReferee[] = [];
  public applicationRefereesLoading: LoadingState = LoadingState.notLoading;
  /** Language chosen by the applicant at the beginning-page, before the application is created. */
  public pendingLanguage: 'en' | 'fr' = 'en';
}

const defaults = {
  selectedApplication: null,
  selectedAdminData: null,
  reviewApplication: null,
  needClinicalDirectorReview: false,
  singleCreatedReferee: null,
  applicationSteps: [],
  applications: [],
  submittedApplications: [],
  eligibilityDetails: null,
  exportedApplications: null,
  mmiApplications: [],
  mmiInterviewees: [],
  reviewPhase2: null,
  flags: [],
  reviewHistory: [],
  reviewHistoryLoading: LoadingState.notLoading,
  previousApplications: [],
  previousApplicationsLoading: LoadingState.notLoading,
  applicationReferees: [],
  applicationRefereesLoading: LoadingState.notLoading,
  loading: LoadingState.notLoading,
  pendingLanguage: 'en' as 'en' | 'fr',
};

/** A referee record from the API carries `id` (toJSON) and `_id`; either names it. */
function refereeId(referee: any): string | undefined {
  const id = referee?.id ?? referee?._id;
  return id == null ? undefined : String(id);
}

function isSameReferee(a: any, b: any): boolean {
  const id = refereeId(a);
  return id != null && id === refereeId(b);
}

@State<ApplicationsStateModel>({
  name: 'applicationState',
  defaults,
})
@Injectable()
export class ApplicationsState {
  applicationService: ApplicationService = inject(ApplicationService);
  // localeService: LocaleService = inject(LocaleService);
  private store = inject(Store); // Inject the store

  /** The application with its References step's referee list rewritten by `update`. */
  private static withReferees(application: ProUserApplication, update: (referees: any[]) => any[]): ProUserApplication {
    const steps: any = application.phase2ApplicationSteps ?? {};
    const references = steps.references ?? {};
    return {
      ...application,
      phase2ApplicationSteps: {
        ...steps,
        references: {
          ...references,
          userData: { ...references.userData, referees: update(references.userData?.referees ?? []) },
        },
      },
    };
  }

  @Selector()
  static getApplications(state: ApplicationsStateModel) {
    return state.applications;
  }

  @Selector()
  static getSubmittedApplications(state: ApplicationsStateModel) {
    return state.submittedApplications;
  }

  @Selector()
  static getFlags(state: ApplicationsStateModel) {
    return state.flags;
  }

  @Selector()
  static getReviewHistory(state: ApplicationsStateModel) {
    return state.reviewHistory;
  }

  @Selector()
  static getReviewHistoryLoading(state: ApplicationsStateModel) {
    return state.reviewHistoryLoading;
  }

  @Selector()
  static getPreviousApplications(state: ApplicationsStateModel) {
    return state.previousApplications;
  }

  @Selector()
  static getPreviousApplicationsLoading(state: ApplicationsStateModel) {
    return state.previousApplicationsLoading;
  }

  @Selector()
  static getApplicationReferees(state: ApplicationsStateModel) {
    return state.applicationReferees;
  }

  @Selector()
  static getApplicationRefereesLoading(state: ApplicationsStateModel) {
    return state.applicationRefereesLoading;
  }

  @Selector()
  static getSelectedApplication(state: ApplicationsStateModel) {
    return state.selectedApplication;
  }

  /** The record the API returned for the last single item created (a referee, an experience). */
  @Selector()
  static getSingleCreatedReferee(state: ApplicationsStateModel) {
    return state.singleCreatedReferee;
  }

  @Selector()
  static getReviewApplication(state: ApplicationsStateModel) {
    return state.reviewApplication;
  }

  /** A withdrawn application is never continuable, whichever phase was
   *  withdrawn — a phase 2 withdrawal must not be undone by a phase 1 status
   *  that still reads In Progress. / Une candidature retirée ne se poursuit jamais. */
  @Selector()
  static canContinueApplication(state: ApplicationsStateModel) {
    if (isApplicationWithdrawn(state.selectedApplication)) return false;
    return state.selectedApplication?.status === ProApplicationStatusEnum.InProgress || state.selectedApplication?.phase2Status === ProApplicationStatusEnum.InProgress;
  }

  @Selector()
  static canSubmitApplication(state: ApplicationsStateModel) {
    return state.selectedApplication?.steps.every((step) => step.finished) ?? false;
  }

  @Selector()
  static getApplicationSteps(state: ApplicationsStateModel) {
    return state.applicationSteps;
  }

  @Selector()
  static continueStepName(state: ApplicationsStateModel): string | null {
    const application = state.selectedApplication;

    if (application == null) {
      return null;
    }

    const step = application.steps.find((s) => !s.finished);
    if (step == null) {
      const lastStep = application.steps[application.steps.length - 1];
      if (lastStep == null) {
        return 'personal-information';
        // return 'pre-screening';
      } else {
        return lastStep.name;
      }
    }

    return step.name;
  }

  @Selector()
  static continuePhase2StepName(state: ApplicationsStateModel): string | null {
    const application = state.selectedApplication;

    const _steps = [
      { name: 'personal-information', id: 1 },
      { name: 'certifications-and-qualifications', id: 2 },
      { name: 'language-proficiency', id: 3 },
      { name: 'postgraduate-training', id: 4 },
      { name: 'rotation-equivalencies', id: 5 },
      { name: 'independent-practice-clinical-experience', id: 6 },
      { name: 'clinical-practice-settings', id: 7 },
      { name: 'ip-patient-population', id: 8 },
      { name: 'currency-practice-clinical-experience', id: 9 },
      { name: 'currency-practice-settings', id: 10 },
      { name: 'currency-of-practice-patient-populations', id: 11 },
      { name: 'references', id: 12 },
      { name: 'information-sharing', id: 13 },
    ];

    // auto select Exam
    if (application == null) {
      return null;
    }

    const exam = application?.phase2Exam;

    const phase2AppSteps = application?.phase2ApplicationSteps == null ? [] : Object.values(application?.phase2ApplicationSteps);

    const phase2Step: any = phase2AppSteps?.find((s: any) => s.name && !s?.completed);

    if (phase2Step == undefined) {
      const lastStep: any = phase2AppSteps.reverse().find((item: any) => item.completed === true);
      if (lastStep == undefined) {
        return 'personal-information';
      } else {
        return lastStep.name;
      }
    }
    return phase2Step.name;
  }

  @Selector()
  static getStepData(state: ApplicationsStateModel) {
    return (stepName: string) => {
      return state.selectedApplication?.steps.find((step) => step.name === stepName)?.data;
    };
  }

  @Selector()
  static getAdminStepData(state: ApplicationsStateModel): ProApplicationReviewStep[] {
    return state.reviewApplication?.application ?? [];
  }

  @Selector()
  static getStepTwoData(state: ApplicationsStateModel) {
    return (stepName: string) => {
      if (state.selectedApplication == null) {
        return null;
      }

      if (state.selectedApplication?.phase2ApplicationSteps == undefined || state.selectedApplication?.phase2ApplicationSteps[stepName as keyof typeof state.selectedApplication.phase2ApplicationSteps] == undefined) return null;

      return state.selectedApplication.phase2ApplicationSteps[stepName as keyof typeof state.selectedApplication.phase2ApplicationSteps];
    };
  }

  @Selector()
  static getPhase2ReviewStep(state: ApplicationsStateModel) {
    return (stepName: string) => {
      if (state.selectedApplication == null) {
        return;
      }

      const reviewStep = state.selectedApplication.phase2ApplicationSteps;

      if (reviewStep[stepName as keyof typeof reviewStep] == undefined) {
        return;
      }

      return reviewStep[stepName as keyof typeof reviewStep];
    };
  }

  @Selector()
  static getPhase2ReviewData(state: ApplicationsStateModel) {
    if (state.reviewPhase2 == null) {
      return;
    }
    return state.reviewPhase2;
  }

  @Selector()
  static getMMIApplications(state: ApplicationsStateModel) {
    return state.mmiApplications;
  }

  @Selector()
  static getMMIInterviewees(state: ApplicationsStateModel) {
    return state.mmiInterviewees;
  }

  @Selector()
  static getLoading(state: ApplicationsStateModel) {
    return state.loading;
  }

  @Selector()
  static getAdminData(state: ApplicationsStateModel) {
    return state.selectedAdminData;
  }

  @Selector()
  static getPendingLanguage(state: ApplicationsStateModel): 'en' | 'fr' {
    return state.pendingLanguage;
  }

  @Action(ApplicationsAction)
  add({ getState, setState }: StateContext<ApplicationsStateModel>, { payload }: ApplicationsAction) {
    const state = getState();
    // setState({ ...state, applications: [...state.applications, payload] });
  }

  @Action(GetApplications)
  getApplications({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: GetApplications) {
    const state = getState();
    // setState({ ...state, loading: LoadingState.loadingList });
    patchState({ loading: LoadingState.loadingList });
    return this.applicationService.getApplications(action.payload).pipe(
      tap((applications: { data: any[] }) => {
        patchState({ applications: applications.data, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(GetMMIApplications)
  getMMIApplications({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: GetMMIApplications) {
    const state = getState();
    // setState({ ...state, loading: LoadingState.loadingList });
    patchState({ loading: LoadingState.loadingList });
    return this.applicationService.getMMIApplications(action.payload).pipe(
      tap((applications: { data: any[] }) => {
        // console.log('Get MMI List', applications.data);
        patchState({ mmiApplications: applications.data, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(GetMMIInterviewees)
  getMMIInterviewees({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: GetMMIInterviewees) {
    const state = getState();
    // setState({ ...state, loading: LoadingState.loadingList });
    patchState({ loading: LoadingState.loadingList });
    return this.applicationService.getMMIInterviewees(action.payload).pipe(
      tap((interviewees: IMMIInterviewees[]) => {
        // console.log('Get MMI Interviewees', interviewees);
        patchState({ mmiInterviewees: interviewees, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(SelectProApplication)
  selectProApplication(ctx: StateContext<ApplicationsStateModel>, action: SelectProApplication | null) {
    ctx.patchState({ selectedApplication: action?.payload });
  }

  @Action(SelectProReviewApplication)
  selectProReviewApplication(ctx: StateContext<ApplicationsStateModel>, action: SelectProReviewApplication) {
    ctx.patchState({ reviewApplication: action.payload });
  }

  @Action(ClearApplications)
  clearApplications({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: ClearApplications) {
    patchState({ applications: [] });
  }

  @Action(AssignApplicationsToCohort)
  assignApplicationsCohort({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: AssignApplicationsToCohort) {
    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.applicationService.assignApplicationToMMICohort(action.payload).pipe(
      tap((result: any) => {
        const state = getState();
        // console.log('Result', result);
        patchState({
          mmiApplications: result,
          loading: LoadingState.notLoading,
        });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(MarkAttendance)
  markAttendance({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: MarkAttendance) {
    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.applicationService.markAttendance(action.payload).pipe(
      tap((attendance: IAttendance) => {
        const state = getState();
        // console.log('Attendance Result', attendance);
        const updatedInterviewees = state.mmiInterviewees.map((interviewee) => (interviewee.applicationId === action.payload.applicationId ? { ...interviewee, attendance: attendance } : interviewee));
        patchState({
          mmiInterviewees: updatedInterviewees,
          loading: LoadingState.notLoading,
        });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(GetProApplications)
  getProApplications({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: GetProApplications) {
    const state = getState();
    patchState({ loading: LoadingState.loadingList });
    return this.applicationService.getProApplications().pipe(
      tap((applications: ProUserApplication[]) => {
        patchState({ applications: applications, loading: LoadingState.notLoading });
        const userId = this.store.selectSnapshot(AuthState.getCurrentUser)!.id;
        const selectedApplication = applications.find((app) => app?.user === userId && (app.status === ProApplicationStatusEnum.InProgress || app.phase2Status === ProApplicationStatusEnum.InProgress)) || null;
        if (selectedApplication) {
          patchState({ selectedApplication });
        }
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(SetPendingApplicationLanguage)
  setPendingApplicationLanguage({ patchState }: StateContext<ApplicationsStateModel>, action: SetPendingApplicationLanguage) {
    patchState({ pendingLanguage: action.payload });
  }

  @Action(SaveApplicationLanguage)
  saveApplicationLanguage({ getState, patchState }: StateContext<ApplicationsStateModel>, action: SaveApplicationLanguage) {
    const { id, language, phase } = action.payload;
    patchState({ pendingLanguage: language });

    return this.applicationService.saveApplicationLanguage(id, { language, phase }).pipe(
      tap((response: any) => {
        const applicationFromResponse = response?.data ?? response?.application;
        if (!applicationFromResponse || typeof applicationFromResponse !== 'object') {
          return;
        }

        const state = getState();
        patchState({
          selectedApplication: state.selectedApplication?.id === id ? { ...state.selectedApplication, ...applicationFromResponse } : state.selectedApplication,
          applications: state.applications.map((application) => (application.id === id ? { ...application, ...applicationFromResponse } : application)),
        });
      }),
      catchError((error) => {
        console.error('Error saving application language', error);
        throw error;
      }),
    );
  }

  @Action(InitiateApplication)
  initiateApplication({ getState, patchState }: StateContext<ApplicationsStateModel>, action: InitiateApplication) {
    const state = getState();
    const userId = this.store.selectSnapshot(AuthState.getCurrentUser)!.id;
    const preScreeningData = this.store.selectSnapshot(PreScreeningState.getPreScreening);
    const examId = action.payload.examId;
    // Use the language from the action payload, fall back to the pending language stored when
    // the applicant made their selection on the beginning page.
    const language = action.payload.language ?? state.pendingLanguage ?? 'en';

    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.applicationService.initializeApplication(examId, userId, preScreeningData, language).pipe(
      tap((selectedApplication: ProUserApplication) => {
        patchState({ selectedApplication, applications: [...state.applications, selectedApplication], loading: LoadingState.notLoading });
        this.store.dispatch(
          new SaveApplicationLanguage({
            id: selectedApplication.id,
            language,
            phase: 'phase1',
          }),
        );
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(SaveStep)
  saveStep(state: StateContext<ApplicationsStateModel>, action: SaveStep) {
    const { step, data, isFinished } = action.payload;
    const userId = this.store.selectSnapshot(AuthState.getCurrentUser)!.id;
    const preScreeningData = this.store.selectSnapshot(PreScreeningState.getPreScreening);
    const examId = this.store.selectSnapshot(ProExamState.getSingleProExam)!.id;
    // const language = this.localeService.locale();
    const language = this.store.selectSnapshot(AuthState.getCurrentLang) ?? 'en';

    const selectedProApplication = state.getState().selectedApplication;

    if (selectedProApplication == null) {
      throw new Error('Select application first before saving step');
    }

    const applicationId = selectedProApplication.id;

    const index = selectedProApplication.steps.findIndex((s: any) => s.name === step);
    if (index === -1) {
      selectedProApplication.steps.push({ name: step, data, finished: isFinished });
    } else {
      selectedProApplication.steps[index].data = data;
      selectedProApplication.steps[index].finished = isFinished;
    }

    const calculatedEligibility = determineEligibility(selectedProApplication, preScreeningData);
    // console.log('eligibility:: info ', calculatedEligibility);
    state.patchState({ loading: LoadingState.loadingAddUpdate });
    return this.applicationService.savePhaseOneApplication(userId, applicationId, examId, calculatedEligibility, preScreeningData, selectedProApplication.steps).pipe(
      tap((selectedApplication: any) => {
        state.patchState({ selectedApplication, loading: LoadingState.notLoading });
        this.store.dispatch(
          new SaveApplicationLanguage({
            id: selectedApplication.id,
            language,
            phase: 'phase1',
          }),
        );
        // console.log('Step saved successfully:', selectedApplication);
      }),
      catchError((error) => {
        console.error('Error submitting Phase One Application', error);
        state.patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(SubmitApplication)
  submitApplication({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: SubmitApplication) {
    const state = getState();
    const applicationId = action.payload;
    patchState({ loading: LoadingState.loadingList });

    return this.applicationService.submitPhaseOneApplication(applicationId).pipe(
      tap((response: { data: ProUserApplication }) => {
        // console.log('Phase One Application submitted successfully', response);
        patchState({ selectedApplication: response.data, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        console.error('Error submitting Phase One Application', error);
        throw error;
      }),
    );
  }

  @Action(UnSubmitApplication)
  unSubmitApplication({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: UnSubmitApplication) {
    const state = getState();
    const applicationId = action.payload;
    patchState({ loading: LoadingState.loadingList });

    return this.applicationService.unSubmitPhaseOneApplication(applicationId).pipe(
      tap((response: { data: ProUserApplication }) => {
        // console.log('Phase One Application unsubmitted successfully', response);
        patchState({ selectedApplication: response.data, loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error unSubmitting Phase One Application', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(UnSubmitPhase2Application)
  unSubmitPhase2Application({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: UnSubmitPhase2Application) {
    const state = getState();
    const applicationId = action.payload;
    patchState({ loading: LoadingState.loadingList });

    return this.applicationService.unSubmitPhaseTwoApplication(applicationId).pipe(
      tap((response: { data: ProUserApplication }) => {
        // console.log('Phase Two Application unsubmitted successfully', response);
        patchState({ selectedApplication: response.data, loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error unSubmitting Phase Two Application', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(MarkInReviewApplication)
  markInReviewApplication({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: MarkInReviewApplication) {
    const state = getState();
    const applicationId = action.payload;
    patchState({ loading: LoadingState.loadingList });

    return this.applicationService.markInReviewApplication(applicationId).pipe(
      tap((response: { data: ProUserApplication }) => {
        // console.log('Phase One Application marked in review successfully', response);
        patchState({ selectedApplication: response.data, loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error marking Phase One Application in review', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(SaveFlag)
  saveFlag({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: SaveFlag) {
    const state = getState();
    const { applicationNo, message, author } = action.payload;

    patchState({ loading: LoadingState.loadingList });

    return this.applicationService.saveFlag({ applicationNo, message }).pipe(
      tap((response: { data: ProUserApplication }) => {
        // console.log('Application Flag saved successfully', response);
        patchState({ selectedApplication: response.data, loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error saving Application Flag', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(GetFlags)
  getFlags({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: GetFlags) {
    const state = getState();
    const applicationNo = action.payload;

    patchState({ loading: LoadingState.loadingList });

    return this.applicationService.getFlags(applicationNo).pipe(
      tap((response: { data: any[] }) => {
        // console.log('Application Flags retrieved successfully', response);
        // patchState({ selectedApplication: response.data, loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error retrieving Application Flags', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(SaveAdminStep)
  saveAdminStep({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: SaveAdminStep) {
    const state = getState();
    const { step, appNo, data } = action.payload;
    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.applicationService.saveAdminStep(step, appNo, data).pipe(
      tap((selectedApplication: any) => {
        // console.log('Admin Step saved successfully', selectedApplication);
        patchState({ selectedApplication, loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error saving Admin Step', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(SavePhase2AdminStep)
  savePhase2AdminStep({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: SavePhase2AdminStep) {
    const state = getState();
    const { step, appNo, data } = action.payload;
    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.applicationService.savePhase2AdminStep(step, appNo, data).pipe(
      tap((response: any) => {
        if (!state.selectedApplication) {
          patchState({ loading: LoadingState.notLoading });
          return;
        }

        this.store.dispatch(new GetAdminApplicationByNo({ applicationNo: appNo }));
        // patchState({ loading: LoadingState.notLoading });

        // const selectedApplication: ProUserApplication = {
        //   ...state.selectedApplication,
        //   phase2ApplicationSteps: {
        //     ...state.selectedApplication.phase2ApplicationSteps,
        //     [step]: response
        //   }
        // };
      }),
      catchError((error: any) => {
        console.error('Error saving Phase 2 Admin Step', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(SetApplicationDecision)
  setApplicationDecision({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: SetApplicationDecision) {
    const state = getState();
    const { applicationNo, examId, decision, comment, conditions, reasons, referralReasons, ineligibleCriterias } = action.payload;
    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.applicationService.setApplicationDecision(applicationNo, examId, decision, comment, conditions, reasons, referralReasons, ineligibleCriterias).pipe(
      tap((selectedApplication: any) => {
        // console.log('Application decision set successfully', selectedApplication);
        patchState({ selectedApplication, loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error setting Application decision', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(SavePhaseTwoStep)
  savePhaseTwoStep(state: StateContext<ApplicationsStateModel>, action: SavePhaseTwoStep) {
    const { step, data, isFinished } = action.payload;
    const examId = this.store.selectSnapshot(ProExamState.getSingleProExam)!.id;
    const selectedProApplication = state.getState().selectedApplication;

    console.log('Step details', action.payload);

    if (selectedProApplication == null) {
      throw new Error('Select application first before saving step');
    }

    const applicationId = selectedProApplication.id;
    const appExamId = selectedProApplication?.phase2Exam?.id;

    // Check if some steps are in existence
    // selectedProApplication.phase2ApplicationSteps[applicationStepKey]

    if (selectedProApplication == null) {
      throw new Error('Select application first before saving step');
    }

    state.patchState({ loading: LoadingState.loadingAddUpdate });
    return this.applicationService
      .savePhaseTwoApplication({
        id: applicationId,
        applicationNo: selectedProApplication.applicationNo,
        examId: examId ?? appExamId,
        data: action.payload.data,
        step: step,
        completed: action.payload.isFinished ?? false,
      })
      .pipe(
        tap((selectedApplication: any) => {
          console.log('Phase Two Step saved successfully:', selectedApplication);
          state.patchState({ selectedApplication: selectedApplication, loading: LoadingState.notLoading });
        }),
        catchError((error) => {
          console.error('Error submitting Phase One Application', error);
          state.patchState({ loading: LoadingState.notLoading });
          throw error;
        }),
      );
  }

  @Action(GetSingleStepApplication)
  getSingleStepApplication({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: GetSingleStepApplication) {
    const state = getState();
    const { step, applicationNo } = action.payload;
    const selectedProApplication = state.selectedApplication;

    if (selectedProApplication == null) {
      throw new Error('Select application first before saving step');
    }

    patchState({ loading: LoadingState.loadingList });
    return this.applicationService.getSingleStepApplication(applicationNo, step).pipe(
      tap((response: any) => {
        console.log('Single Step Application retrieved successfully', response);
        // console.log(response)
        // state.selectedApplication.phase2ApplicationSteps[stepName as keyof typeof state.selectedApplication.phase2ApplicationSteps]
        patchState({
          // applicationSteps: [...state.applicationSteps.filter(s => s.name !== step), { name: step, ...response.application }],
          // selectedApplication: response.application,
          loading: LoadingState.notLoading,
        });
      }),
      catchError((error: any) => {
        console.error('Error retrieving Single Step Application', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(GetPhase2ApplicationRecord)
  getPhase2ApplicationRecord({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: GetPhase2ApplicationRecord) {
    const state = getState();
    const { step, applicationNo } = action.payload;
    patchState({ loading: LoadingState.loadingList });

    return this.applicationService.getPhase2ApplicationRecords(step, applicationNo).pipe(
      tap((response: { data: any }) => {
        patchState({ reviewPhase2: { ...state.reviewPhase2, ...response.data }, loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(SubmitPro2Application)
  submitPro2Application({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: SubmitPro2Application) {
    const state = getState();
    const applicationId = action.payload;
    const examId = this.store.selectSnapshot(ProExamState.getSingleProExam)!.id;
    const selectedProApplication = state.selectedApplication;

    if (selectedProApplication == null) {
      throw new Error('Select application first before saving step');
    }

    patchState({ loading: LoadingState.loadingList });
    return this.applicationService.submitPro2Application(selectedProApplication, examId).pipe(
      // The submit endpoint only returns a `{ data: "Application Submitted!" }` message,
      // not the application. Refetch the applicant's applications so the store gets the
      // real server state (status, submitted date, applicationNo). If the refetch fails,
      // fall back to the existing application with the status reflected locally — a
      // refetch failure must not make a successful submission look failed.
      switchMap(() =>
        this.applicationService.getProApplications().pipe(
          map((applications: ProUserApplication[]) => ({
            applications,
            refreshed: applications.find((app) => app.id === selectedProApplication.id) || null,
          })),
          catchError(() => of({ applications: null as ProUserApplication[] | null, refreshed: null })),
        ),
      ),
      tap(({ applications, refreshed }) => {
        patchState({
          ...(applications ? { applications } : {}),
          selectedApplication: refreshed ?? { ...selectedProApplication, phase2Status: ProApplicationStatusEnum.Submitted },
          loading: LoadingState.notLoading,
        });
      }),
      catchError((error) => {
        console.error('Error submitting Phase Two Application', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(OpenPhase2Application)
  openPhase2Application(state: StateContext<ApplicationsStateModel>, action: OpenPhase2Application) {
    state.patchState({ loading: LoadingState.loadingList });

    return this.applicationService.openPhase2Application(action.payload).pipe(
      tap((selectedApplication: ProUserApplication) => {
        if (selectedApplication && typeof selectedApplication !== 'string' && selectedApplication.id) {
          state.patchState({ selectedApplication, loading: LoadingState.notLoading });
        } else {
          state.patchState({ loading: LoadingState.notLoading });
        }
      }),
      catchError((error) => {
        state.patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(GetAdminData)
  getAdminData({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: GetAdminData) {
    const state = getState();
    const applicationNo = action.payload.applicationNo;

    patchState({ loading: LoadingState.loadingList });

    return this.applicationService.getAdminData(applicationNo).pipe(
      tap((response: { data: any }) => {
        // console.log('Admin data retrieved successfully', response);
        patchState({ selectedAdminData: response.data, loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error retrieving admin data', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(GetApplicationRecord)
  getApplicationRecord({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: GetApplicationRecord) {
    const state = getState();
    const { step, applicationNo } = action.payload;

    patchState({ loading: LoadingState.loadingList });

    return this.applicationService.getApplicationRecords(step, applicationNo).pipe(
      tap((response: { data: { adminData: any; steps: any } }) => {
        // console.log('Application record retrieved successfully', response);
        patchState({
          reviewApplication: {
            ...state.reviewApplication,
            application: [{ name: step, ...response.data }],
          } as ProApplicationReview,
          loading: LoadingState.notLoading,
        });
      }),
      catchError((error: any) => {
        console.error('Error retrieving application record', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(SaveSinglePhaseTwoStep)
  SaveSinglePhaseTwoStep({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: SaveSinglePhaseTwoStep) {
    const state = getState();
    const { step, data, isFinished } = action.payload;
    const userId = this.store.selectSnapshot(AuthState.getCurrentUser)!.id;
    const examId = this.store.selectSnapshot(ProExamState.getSingleProExam)!.id;

    const selectedProApplication = state.selectedApplication;
    if (selectedProApplication == null) {
      throw new Error('Select application first before saving step');
    }

    patchState({ loading: LoadingState.loadingAddUpdate });

    return this.applicationService.saveSinglePhaseTwoStep(action.payload.step, action.payload.data).pipe(
      tap((response: { data: any }) => {
        console.log('Response from saving single phase two step:', response);

        console.log('Selected application before updating single step:', step);

        // Referees live in references.userData.referees, which the step-name lookup
        // below never finds for 'referee'. Without this, `loading` stayed set, the
        // references page never learnt the new referee's id, and the store kept the
        // old referee list.
        if (step === 'referee') {
          const created = response.data;
          const current = getState().selectedApplication ?? selectedProApplication;
          patchState({
            loading: LoadingState.notLoading,
            singleCreatedReferee: created,
            selectedApplication: ApplicationsState.withReferees(current, (referees) => [...referees.filter((referee) => !isSameReferee(referee, created)), created]),
          });
          return;
        }

        if (selectedProApplication.phase2ApplicationSteps) {
          let stepKey = Object.keys(selectedProApplication.phase2ApplicationSteps).find((key: string) => (selectedProApplication.phase2ApplicationSteps as any)[key].name === step);

          if (stepKey == undefined) {
            stepKey = Object.keys(selectedProApplication.phase2ApplicationSteps).find((key: string) => (selectedProApplication.phase2ApplicationSteps as any)[key].name?.includes(step));
            console.log('Step key found with partial match:', stepKey);

            if (!stepKey && step === 'rotation-equivalence') {
              stepKey = 'rotationEquivalencies';
            }
          }

          if (stepKey) {
            const currentStep = (selectedProApplication.phase2ApplicationSteps as any)[stepKey];
            console.log('Current step before update:', currentStep);

            if (!currentStep) {
              const updatedApplication = {
                ...selectedProApplication,
                phase2ApplicationSteps: {
                  ...selectedProApplication.phase2ApplicationSteps,
                  [stepKey]: {
                    name: step,
                    completed: false,
                    userData: stepKey === 'rotationEquivalencies' ? [response.data] : response.data,
                  },
                },
              };

              patchState({ loading: LoadingState.notLoading, selectedApplication: updatedApplication, singleCreatedReferee: response.data });
              return;
            }

            console.log('Previous data to be added:', currentStep.userData);
            if (currentStep.userData && typeof currentStep.userData === 'object' && 'clinicalExperiences' in currentStep.userData && Array.isArray(currentStep.userData.clinicalExperiences)) {
              const updatedClinicalExperiences = [...currentStep.userData.clinicalExperiences, response.data];
              console.log('Updated clinicalExperiences after adding new item:', updatedClinicalExperiences);

              const updatedApplication = {
                ...selectedProApplication,
                phase2ApplicationSteps: {
                  ...selectedProApplication.phase2ApplicationSteps,
                  [stepKey]: {
                    ...currentStep,
                    userData: { ...currentStep.userData, clinicalExperiences: updatedClinicalExperiences },
                  },
                },
              };

              patchState({ loading: LoadingState.notLoading, selectedApplication: updatedApplication, singleCreatedReferee: response.data });
            } else if (currentStep.userData && typeof currentStep.userData === 'object' && stepKey === 'rotationEquivalencies' && Array.isArray(currentStep.userData)) {
              const updatedRotationEquivalencies = [...currentStep.userData, response.data];
              console.log('Updated rotationEquivalencies after adding new item:', updatedRotationEquivalencies);

              const updatedApplication = {
                ...selectedProApplication,
                phase2ApplicationSteps: {
                  ...selectedProApplication.phase2ApplicationSteps,
                  [stepKey]: {
                    ...currentStep,
                    userData: updatedRotationEquivalencies,
                  },
                },
              };

              patchState({ loading: LoadingState.notLoading, selectedApplication: updatedApplication, singleCreatedReferee: response.data });
            } else if (Array.isArray(currentStep.userData)) {
              const updatedUserData = [...currentStep.userData, response.data];

              const updatedApplication = {
                ...selectedProApplication,
                phase2ApplicationSteps: {
                  ...selectedProApplication.phase2ApplicationSteps,
                  [stepKey]: {
                    ...currentStep,
                    userData: updatedUserData,
                  },
                },
              };
              patchState({ loading: LoadingState.notLoading, selectedApplication: updatedApplication, singleCreatedReferee: response.data });
            }
          }
        }
      }),
      catchError((error: any) => {
        console.error('Error retrieving flag details', error);
        patchState({ loading: LoadingState.notLoading, singleCreatedReferee: null });

        throw error;
      }),
    );
  }

  @Action(UpdateSinglePhaseTwoStep)
  UpdateSinglePhaseTwoStep({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: SaveSinglePhaseTwoStep) {
    const state = getState();
    const { step, data, isFinished } = action.payload;
    const userId = this.store.selectSnapshot(AuthState.getCurrentUser)!.id;
    const examId = this.store.selectSnapshot(ProExamState.getSingleProExam)!.id;
    const selectedProApplication = state.selectedApplication;
    if (selectedProApplication == null) {
      throw new Error('Select application first before saving step');
    }
    patchState({ loading: LoadingState.loadingAddUpdate });

    return this.applicationService.updateSinglePhaseTwoStep(action.payload.step, action.payload.data).pipe(
      tap((response: { data: any }) => {
        console.log('Single Phase Two Step updated successfully', response);

        // See SaveSinglePhaseTwoStep: referees are not found by step name.
        if (step === 'referee') {
          const updated = response.data;
          const current = getState().selectedApplication;
          patchState({
            loading: LoadingState.notLoading,
            ...(current && updated ? { selectedApplication: ApplicationsState.withReferees(current, (referees) => referees.map((referee) => (isSameReferee(referee, updated) ? updated : referee))) } : {}),
          });
          return;
        }

        if (selectedProApplication.phase2ApplicationSteps) {
          let stepKey = Object.keys(selectedProApplication.phase2ApplicationSteps).find((key: string) => (selectedProApplication.phase2ApplicationSteps as any)[key].name === step);

          // Fallback: if stepKey not found, try partial matching
          if (!stepKey) {
            if (step === 'independent-practice') {
              stepKey = Object.keys(selectedProApplication.phase2ApplicationSteps).find((key: string) => (selectedProApplication.phase2ApplicationSteps as any)[key].name === 'independent-practice-clinical-experience');
            } else if (step === 'currency') {
              stepKey = Object.keys(selectedProApplication.phase2ApplicationSteps).find((key: string) => (selectedProApplication.phase2ApplicationSteps as any)[key].name === 'currency-practice-clinical-experience');
            }
          }

          if (stepKey) {
            const currentStep = (selectedProApplication.phase2ApplicationSteps as any)[stepKey];
            console.log('Current step before update:', currentStep);
            if (Array.isArray(currentStep.userData)) {
              const updatedUserData = currentStep.userData.map((item: any) => (item._id === response.data._id ? response.data : item));
              console.log('Updated userData after update:', updatedUserData);

              const updatedApplication = {
                ...selectedProApplication,
                phase2ApplicationSteps: {
                  ...selectedProApplication.phase2ApplicationSteps,
                  [stepKey]: {
                    ...currentStep,
                    userData: updatedUserData,
                  },
                },
              };
              patchState({ selectedApplication: updatedApplication, loading: LoadingState.notLoading });
              return;
            }

            // Handle userData with clinicalExperiences array
            if (currentStep.userData && typeof currentStep.userData === 'object' && 'clinicalExperiences' in currentStep.userData && Array.isArray(currentStep.userData.clinicalExperiences)) {
              const updatedClinicalExperiences = currentStep.userData.clinicalExperiences.map((item: any) => (item._id === response.data._id ? response.data : item));

              console.log('Updated clinicalExperiences after update:', updatedClinicalExperiences);

              const updatedApplication = {
                ...selectedProApplication,
                phase2ApplicationSteps: {
                  ...selectedProApplication.phase2ApplicationSteps,
                  [stepKey]: {
                    ...currentStep,
                    userData: {
                      ...currentStep.userData,
                      clinicalExperiences: updatedClinicalExperiences,
                    },
                  },
                },
              };
              patchState({ selectedApplication: updatedApplication, loading: LoadingState.notLoading });
              return;
            }

            // Handle userData with currencyOfPracticeExperiences array
            if (currentStep.userData && typeof currentStep.userData === 'object' && 'currencyOfPracticeExperiences' in currentStep.userData && Array.isArray(currentStep.userData.currencyOfPracticeExperiences)) {
              const updatedCurrencyOfPracticeExperiences = currentStep.userData.currencyOfPracticeExperiences.map((item: any) => (item._id === response.data._id ? response.data : item));

              const updatedApplication = {
                ...selectedProApplication,
                phase2ApplicationSteps: {
                  ...selectedProApplication.phase2ApplicationSteps,
                  [stepKey]: {
                    ...currentStep,
                    userData: {
                      ...currentStep.userData,
                      currencyOfPracticeExperiences: updatedCurrencyOfPracticeExperiences,
                    },
                  },
                },
              };
              patchState({ selectedApplication: updatedApplication, loading: LoadingState.notLoading });
              return;
            }
          }
        }
        patchState({ loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error updating single phase two step', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(DeleteReferees)
  deleteReferees({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: DeleteReferees) {
    const { applicationType, applicationIds } = action.payload;
    patchState({ loading: LoadingState.loadingAddUpdate });

    return this.applicationService.deleteReferees(applicationIds, applicationType).pipe(
      tap((response: { data: any }[]) => {
        const state = getState();
        const selectedProApplication = state.selectedApplication;

        // See SaveSinglePhaseTwoStep: referees are not found by step name, so a
        // removed referee used to stay in the store and come back on the page.
        if (applicationType === 'referee') {
          const removed = new Set(applicationIds.map(String));
          patchState({
            loading: LoadingState.notLoading,
            ...(selectedProApplication ? { selectedApplication: ApplicationsState.withReferees(selectedProApplication, (referees) => referees.filter((referee) => !removed.has(String(refereeId(referee))))) } : {}),
          });
          return;
        }

        if (selectedProApplication && selectedProApplication.phase2ApplicationSteps) {
          let stepKey = Object.keys(selectedProApplication.phase2ApplicationSteps).find((key: string) => (selectedProApplication.phase2ApplicationSteps as any)[key].name === action.payload.applicationType);

          // Fallback: if stepKey not found, try partial matching
          if (!stepKey) {
            if (action.payload.applicationType === 'independent-practice') {
              stepKey = Object.keys(selectedProApplication.phase2ApplicationSteps).find((key: string) => (selectedProApplication.phase2ApplicationSteps as any)[key].name === 'independent-practice-clinical-experience');
            } else if (action.payload.applicationType === 'currency') {
              stepKey = Object.keys(selectedProApplication.phase2ApplicationSteps).find((key: string) => (selectedProApplication.phase2ApplicationSteps as any)[key].name === 'currency-practice-clinical-experience');
            }
          }

          if (stepKey) {
            const currentStep = (selectedProApplication.phase2ApplicationSteps as any)[stepKey];

            if (currentStep.userData && 'clinicalExperiences' in currentStep.userData && Array.isArray(currentStep.userData.clinicalExperiences)) {
              const updatedClinicalExperiences = currentStep.userData.clinicalExperiences.filter((item: any) => !action.payload.applicationIds.includes(item._id));

              const updatedApplication = {
                ...selectedProApplication,
                phase2ApplicationSteps: {
                  ...selectedProApplication.phase2ApplicationSteps,
                  [stepKey]: {
                    ...currentStep,
                    userData: {
                      ...currentStep.userData,
                      clinicalExperiences: updatedClinicalExperiences,
                    },
                  },
                },
              };
              patchState({ selectedApplication: updatedApplication, loading: LoadingState.notLoading });
            }
          }
        }
        console.log('Referee deleted successfully', response);
        patchState({ loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error deleting referee', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(GetFlagDetails)
  getFlagDetails({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: GetFlagDetails) {
    const state = getState();
    const applicationNo = action.payload;

    patchState({ loading: LoadingState.loadingList });

    return this.applicationService.getFlagDetails(applicationNo).pipe(
      tap((response: { data: any }) => {
        // console.log('Flag details retrieved successfully', response);
        patchState({ flags: response.data, loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error retrieving flag details', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(UpdateFlagResolution)
  updateFlagResolution({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: UpdateFlagResolution) {
    const state = getState();
    const applicationNo = action.payload;
    const flags = action.flags;

    patchState({ loading: LoadingState.loadingAddUpdate });

    return this.applicationService.updateFlagResolution(applicationNo, flags).pipe(
      tap((response: any) => {
        // console.log('Flag resolution updated successfully', response);
        patchState({ loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error updating flag resolution', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(GetAllProExamApplications)
  getAllProExamApplications({ patchState }: StateContext<ApplicationsStateModel>, action: GetAllProExamApplications) {
    if (!action.payload.stealth) {
      patchState({ loading: LoadingState.loadingList });
    }
    return this.applicationService.getAllApplications(action.payload).pipe(
      tap((applications: any) => {
        if (action.payload.status === ProApplicationStatusEnum.Submitted) {
          patchState({ submittedApplications: applications, loading: LoadingState.notLoading });
          return;
        }
        patchState({ applications, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(GetAllProExamApplicationsSilent)
  getAllProExamApplicationsSilent({ patchState }: StateContext<ApplicationsStateModel>, action: GetAllProExamApplicationsSilent) {
    return this.applicationService.getAllApplications(action.payload).pipe(
      tap((applications: any) => {
        if (action.payload.status === ProApplicationStatusEnum.Submitted) {
          patchState({ submittedApplications: applications, loading: LoadingState.notLoading });
          return;
        }
        patchState({ applications, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(GetExportedApplications)
  getExportedApplications({ patchState }: StateContext<ApplicationsStateModel>, action: GetExportedApplications) {
    patchState({ loading: LoadingState.loadingList });

    return this.applicationService.getExportedApplications(action.payload.examId).pipe(
      tap((response: any) => {
        // console.log('Exported applications retrieved successfully', response);
        patchState({ exportedApplications: response.data, loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error retrieving exported applications', error);
        throw error;
      }),
    );
  }

  @Action(GetAllApplicationSteps)
  getAllApplicationSteps({ getState, patchState }: StateContext<ApplicationsStateModel>, action: GetAllApplicationSteps) {
    const state = getState();
    const applicationId = action.payload;

    patchState({ loading: LoadingState.loadingList });

    return this.applicationService.getAllApplicationSteps(applicationId).pipe(
      tap((response: any) => {
        // console.log('All application steps retrieved successfully', response);
        patchState({ applicationSteps: response, loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error retrieving all application steps', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(ClearAllApplicationSteps)
  clearAllApplicationSteps({ getState, patchState }: StateContext<ApplicationsStateModel>, action: ClearAllApplicationSteps) {
    patchState({ applicationSteps: [], loading: LoadingState.notLoading });
  }

  @Action(GetEligibilityDetails)
  getEligibilityDetails({ patchState }: StateContext<ApplicationsStateModel>, action: GetEligibilityDetails) {
    patchState({ loading: LoadingState.loadingList });
    return this.applicationService.getEligibilityDetails(action.payload).pipe(
      tap((response: any) => {
        // console.log('Eligibility details retrieved successfully', response);
        patchState({ eligibilityDetails: response.data, loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error retrieving eligibility details', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(GetReviewHistory)
  getReviewHistory({ patchState }: StateContext<ApplicationsStateModel>, action: GetReviewHistory) {
    // Clear first so a slow response never shows the previously opened row's trail.
    // Vidé d'abord pour ne jamais afficher la piste de la ligne précédente.
    patchState({ reviewHistory: [], reviewHistoryLoading: LoadingState.loadingList });

    return this.applicationService.getReviewHistory(action.payload.applicationNo, action.payload.phase).pipe(
      tap((reviewHistory: ReviewHistoryEntry[]) => {
        patchState({ reviewHistory, reviewHistoryLoading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error retrieving review history', error);
        patchState({ reviewHistoryLoading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(GetPreviousApplications)
  getPreviousApplications({ patchState }: StateContext<ApplicationsStateModel>, action: GetPreviousApplications) {
    patchState({ previousApplications: [], previousApplicationsLoading: LoadingState.loadingList });

    return this.applicationService.getPreviousApplications(action.payload.userId, action.payload.examId).pipe(
      tap((previousApplications: PreviousApplication[]) => {
        patchState({ previousApplications, previousApplicationsLoading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error retrieving previous applications', error);
        patchState({ previousApplicationsLoading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(SetNeedClinicalDirector)
  setNeedClinicalDirector({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: SetNeedClinicalDirector) {
    patchState({ loading: LoadingState.notLoading });

    const state = getState();
    const selectedApplication = state.reviewApplication;
    const payload = {
      applicationId: selectedApplication?._id!,
      examId: action.payload.examId,
      needClinicalDirectorReview: action.payload.needClinicalDirectorReview,
    };
    return this.applicationService.setNeedClinicalDirectorReview(payload).pipe(
      tap((response: any) => {
        patchState({ loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error retrieving eligibility details', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(AddReferenceNote)
  addReferenceNote({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: AddReferenceNote) {
    patchState({ loading: LoadingState.notLoading });
    const state = getState();

    return this.applicationService.addReferenceNote(action.payload.step, action.payload.data).pipe(
      tap((response: any) => {
        patchState({ loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error adding reference note', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  // ResendSingleReferenceEmail, ResendReferenceEmail, GetReferencesByApplicationId

  @Action(ResendSingleReferenceEmail)
  resendSingleReferenceEmail({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: ResendSingleReferenceEmail) {
    patchState({ loading: LoadingState.notLoading });
    const state = getState();
    return this.applicationService.resendSingleReferenceEmail(action.payload).pipe(
      tap((response: any) => {
        patchState({ loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error resending single reference email', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(ResendReferenceEmail)
  resendReferenceEmail({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: ResendReferenceEmail) {
    patchState({ loading: LoadingState.notLoading });
    const state = getState();
    return this.applicationService.resendReferenceEmail(action.payload).pipe(
      tap((response: any) => {
        patchState({ loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error resending reference email', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(GetAdminApplicationByNo)
  getAdminApplicationByNo({ patchState }: StateContext<ApplicationsStateModel>, action: GetAdminApplicationByNo) {
    patchState({ loading: LoadingState.loadingList });
    return this.applicationService.getAdminApplicationByNo(action.payload.applicationNo).pipe(
      tap((response: any) => {
        console.log('Response Get Admin', response);
        patchState({ selectedApplication: response.data, loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error retrieving admin application by number', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(ResendSingleRotationEquivalences)
  resendSingleRotationEquivalences({ getState, patchState, setState }: StateContext<ApplicationsStateModel>, action: ResendSingleRotationEquivalences) {
    patchState({ loading: LoadingState.notLoading });
    const state = getState();
    return this.applicationService.resendSingleRotationEquivalences(action.payload).pipe(
      tap((response: any) => {
        patchState({ loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error resending single rotation equivalences email', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(GetApplicationReferees)
  getApplicationReferees({ patchState }: StateContext<ApplicationsStateModel>, action: GetApplicationReferees) {
    // Cleared first so a slow response never shows the previously opened row's referees.
    // Vidé d'abord pour ne jamais afficher les répondants de la ligne précédente.
    patchState({ applicationReferees: [], applicationRefereesLoading: LoadingState.loadingList });

    return this.applicationService.getApplicationReferees(action.payload.applicationNo).pipe(
      tap((applicationReferees: ApplicationReferee[]) => {
        patchState({ applicationReferees, applicationRefereesLoading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error retrieving application referees', error);
        patchState({ applicationRefereesLoading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(AdminResendRefereeEmail)
  adminResendRefereeEmail({ getState, patchState }: StateContext<ApplicationsStateModel>, action: AdminResendRefereeEmail) {
    return this.applicationService.adminResendRefereeEmail(action.payload).pipe(
      tap((result: { data: string, sentAt: string }) => {
        // Stamp the row that was just nudged so the dialog shows the new send
        // date without refetching the whole list.
        // La ligne relancée est horodatée sur place, sans recharger la liste.
        const sentAt = result?.sentAt ?? new Date().toISOString();
        patchState({
          applicationReferees: getState().applicationReferees.map((referee) =>
            referee.id === action.payload.refereeId ? { ...referee, lastReminderSentAt: sentAt } : referee,
          ),
        });
      }),
      catchError((error: any) => {
        console.error('Error resending referee email', error);
        throw error;
      }),
    );
  }

  @Action(ResendApplicantRefereeEmail)
  resendApplicantRefereeEmail({ patchState }: StateContext<ApplicationsStateModel>, action: ResendApplicantRefereeEmail) {
    patchState({ loading: LoadingState.loadingAddUpdate });
    return this.applicationService.resendApplicantRefereeEmail(action.payload).pipe(
      tap(() => {
        patchState({ loading: LoadingState.notLoading });
      }),
      catchError((error: any) => {
        console.error('Error resending applicant referee email', error);
        patchState({ loading: LoadingState.notLoading });
        throw error;
      }),
    );
  }

  @Action(ClearSelectedProApplication)
  clearSelectedProApplication({ patchState }: StateContext<ApplicationsStateModel>) {
    patchState({ selectedApplication: null });
  }
}
